import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { formatApiErrorDetail } from "@/components/pepmuse/AuthContext";
import { Reveal, Eyebrow } from "@/components/pepmuse/Reveal";

const API = process.env.REACT_APP_BACKEND_URL;

const fieldClass =
  "w-full rounded-xl border border-white/10 bg-ink/70 px-4 py-3 text-sm text-paper placeholder:text-paper/35 transition-[border-color,box-shadow] duration-200 focus:border-lilac/60 focus:outline-none focus:ring-2 focus:ring-lilac/30";
const labelClass = "mb-1.5 block text-sm font-bold text-paper/85";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Reset password — PepMuse";
    return () => {
      document.title = "PepMuse — Your peptide brand. Ready to launch.";
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(formatApiErrorDetail(data.detail));
        return;
      }
      setDone(true);
    } catch {
      setError("Couldn't reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="reset-password-page" className="pb-24 pt-[120px] sm:pt-[150px]">
      <div className="mx-auto w-full max-w-md px-5 sm:px-8">
        <Reveal>
          <div className="rounded-3xl border border-white/10 bg-card p-7 sm:p-9">
            <Eyebrow testId="reset-eyebrow">Account</Eyebrow>
            {done ? (
              <div className="mt-6 text-center">
                <CheckCircle2 className="mx-auto h-11 w-11 text-lilac" aria-hidden="true" />
                <h1 className="mt-4 font-display text-2xl font-extrabold text-paper">Password updated.</h1>
                <p data-testid="reset-success" className="mt-2 text-sm text-paper/55">
                  You're all set — sign in with your new password.
                </p>
                <Link
                  to="/account"
                  data-testid="reset-success-signin-link"
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-lilac px-7 py-3 text-sm font-bold text-ink transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-lilac-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                >
                  Go to sign in
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            ) : !token ? (
              <div className="mt-6">
                <h1 className="font-display text-2xl font-extrabold text-paper">This link isn't complete.</h1>
                <p data-testid="reset-invalid" className="mt-2 text-sm text-paper/55">
                  The reset link looks broken. Request a fresh one from the sign-in page.
                </p>
                <Link
                  to="/account"
                  data-testid="reset-invalid-signin-link"
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-7 py-3 text-sm font-bold text-paper transition-colors duration-200 hover:border-lilac/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                >
                  Back to sign in
                </Link>
              </div>
            ) : (
              <>
                <h1 className="mt-6 font-display text-2xl font-extrabold text-paper">Choose a new password.</h1>
                <p className="mt-2 text-sm text-paper/55">At least 8 characters. The link works once, within an hour.</p>
                {error && (
                  <p role="alert" data-testid="reset-error" className="mt-5 rounded-xl border border-red-400/30 bg-red-400/10 p-3.5 text-sm text-red-200">
                    {error}
                  </p>
                )}
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="reset-password" className={labelClass}>New password</label>
                    <input
                      id="reset-password"
                      data-testid="reset-password-input"
                      type="password"
                      autoComplete="new-password"
                      required
                      minLength={8}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError("");
                      }}
                      placeholder="8+ characters"
                      className={fieldClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="reset-confirm" className={labelClass}>Confirm password</label>
                    <input
                      id="reset-confirm"
                      data-testid="reset-confirm-input"
                      type="password"
                      autoComplete="new-password"
                      required
                      minLength={8}
                      value={confirm}
                      onChange={(e) => {
                        setConfirm(e.target.value);
                        setError("");
                      }}
                      placeholder="Repeat it"
                      className={fieldClass}
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="reset-submit-button"
                    disabled={busy}
                    className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-lilac px-7 py-3.5 text-sm font-bold text-ink transition-[transform,box-shadow,background-color,opacity] duration-200 ease-out hover:-translate-y-0.5 hover:bg-lilac-bright hover:shadow-[0_16px_36px_-12px_rgba(184,167,232,0.55)] active:translate-y-0 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-card disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {busy ? "Updating…" : "Update password"}
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                  </button>
                </form>
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
